import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, ListOrdered, Plus, Users2 } from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { OverviewToolbar } from "../components/layout/OverviewToolbar";
import { Modal } from "../components/ui/Modal";
import {
  loadViewMode,
  saveViewMode,
  useFitsMultipleColumns,
  ViewToggle,
  type ViewMode,
} from "../components/layout/ViewToggle";
import {
  huntgroupsApi,
  type HuntGroupListItem,
} from "../api/huntgroupsApi";
import { HttpError } from "../api/http";

const HG_CARD_MIN = 260;

type LoadState =
  | { status: "loading" }
  | { status: "ready"; items: HuntGroupListItem[] }
  | { status: "error"; message: string };

/**
 * Customer huntgroups overview. Reads spHuntGroup_List_V02 via the .NET
 * HuntGroupsController and presents the same columns the legacy portal
 * does: name + queue distribution + queue limit description + a green
 * dot when at least one assigned destination is currently signed in.
 */
export function HuntGroupsPage() {
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [view, setView] = useState<ViewMode>(() => loadViewMode("huntgroups"));
  const navigate = useNavigate();
  const listRef = useRef<HTMLDivElement>(null);
  const canShowToggle = useFitsMultipleColumns(listRef, HG_CARD_MIN);

  const filtered = useMemo(() => {
    if (state.status !== "ready") return [] as HuntGroupListItem[];
    const q = search.trim().toLowerCase();
    if (!q) return state.items;
    return state.items.filter(
      (h) =>
        h.name.toLowerCase().includes(q) ||
        h.distribution.toLowerCase().includes(q) ||
        h.limit.toLowerCase().includes(q),
    );
  }, [state, search]);

  useEffect(() => {
    let active = true;
    huntgroupsApi
      .list()
      .then((response) => {
        if (!active) return;
        setState({ status: "ready", items: response.items ?? [] });
      })
      .catch((err: unknown) => {
        if (!active) return;
        const message =
          err instanceof Error ? err.message : "Failed to load huntgroups";
        setState({ status: "error", message });
      });
    return () => {
      active = false;
    };
  }, []);

  const existingNames = useMemo(() => {
    if (state.status !== "ready") return new Set<string>();
    return new Set(state.items.map((x) => x.name.trim().toLowerCase()));
  }, [state]);

  return (
    <>
      <PageHeader
        title="Huntgroups"
        actions={
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setModalOpen(true)}
            style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
          >
            <Plus size={16} />
            New huntgroup
          </button>
        }
      />
      <div className="dashboard">
        {state.status === "loading" && (
          <div className="tile" style={{ color: "var(--portal-muted)" }}>
            Loading huntgroups…
          </div>
        )}
        {state.status === "error" && (
          <div
            className="tile"
            style={{
              borderColor: "#e74c3c",
              color: "#c0392b",
              background: "#fdecea",
            }}
          >
            <strong>Something went wrong.</strong>
            <div style={{ marginTop: 6, fontSize: "0.9rem" }}>
              {state.message}
            </div>
          </div>
        )}
        {state.status === "ready" && state.items.length === 0 && <EmptyState />}
        {state.status === "ready" && state.items.length > 0 && (
          <>
            <OverviewToolbar
              search={search}
              onSearch={setSearch}
              placeholder="Search by name or distribution"
              count={{ matched: filtered.length, total: state.items.length }}
              trailing={
                canShowToggle ? (
                  <ViewToggle
                    value={view}
                    onChange={(next) => {
                      setView(next);
                      saveViewMode("huntgroups", next);
                    }}
                  />
                ) : null
              }
            />
            <div ref={listRef}>
              {filtered.length === 0 ? (
                <div className="tile" style={{ color: "var(--portal-muted)" }}>
                  No huntgroups match "{search}".
                </div>
              ) : view === "card" || !canShowToggle ? (
                <HuntGroupsCardGrid items={filtered} />
              ) : (
                <HuntGroupsRowList items={filtered} />
              )}
            </div>
          </>
        )}
      </div>

      <CreateHuntGroupModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        existingNames={existingNames}
        onCreated={(id) => {
          setModalOpen(false);
          navigate(`/huntgroups/${id}`);
        }}
      />
    </>
  );
}

function CreateHuntGroupModal({
  open,
  onClose,
  existingNames,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  existingNames: Set<string>;
  onCreated: (id: number) => void;
}) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state between opens so leftover input/errors from a previous
  // attempt don't bleed into a fresh creation flow.
  useEffect(() => {
    if (!open) {
      setName("");
      setBusy(false);
      setError(null);
    }
  }, [open]);

  const trimmed = name.trim();
  const clientDuplicate =
    trimmed.length > 0 && existingNames.has(trimmed.toLowerCase());
  const canSubmit = !busy && trimmed.length > 0;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      // Default payload mirrors what the old "instant create" path used —
      // the detail editor is where the user refines distribution, queue
      // limits, recording etc. Only the name is collected here because
      // that's the single field the user must uniquely decide up front.
      const created = await huntgroupsApi.create({
        hgName: trimmed,
        hgRef: "",
        description: "",
        queueDistributionTypeId: 1,
        queueLimitTypeId: 0,
        queueLimit: 0,
        postCallHandle: 0,
        recordCalls: false,
      });
      onCreated(created.huntGroupId);
    } catch (err) {
      if (err instanceof HttpError && err.status === 409) {
        setError("A huntgroup with that name already exists.");
      } else {
        const fallback =
          err instanceof Error ? err.message : "Failed to create huntgroup";
        const apiDetail =
          err instanceof HttpError && typeof err.data === "object" && err.data
            ? (err.data as { error?: string; detail?: string }).detail ?? null
            : null;
        setError(apiDetail ?? fallback);
      }
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New huntgroup"
      busy={busy}
      footer={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={submit}
            disabled={!canSubmit}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <Plus size={16} />
            {busy ? "Creating…" : "Create"}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        style={{ display: "grid", gap: 10 }}
      >
        <label style={{ display: "grid", gap: 4 }}>
          <span
            style={{
              fontSize: "0.8rem",
              color: "var(--portal-muted)",
              fontWeight: 600,
            }}
          >
            Name
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
            placeholder="e.g. Sales queue"
            maxLength={50}
            autoFocus
            style={{
              padding: "8px 12px",
              border: "1px solid var(--portal-border)",
              borderRadius: 6,
              fontSize: "0.95rem",
              fontFamily: "inherit",
              background: "#ffffff",
            }}
          />
        </label>
        {clientDuplicate && !error && (
          <div style={{ fontSize: "0.85rem", color: "#8a6d00" }}>
            A huntgroup named "{trimmed}" already exists. Pick a different
            name.
          </div>
        )}
        {error && (
          <div style={{ fontSize: "0.85rem", color: "#c0392b" }}>{error}</div>
        )}
        <button type="submit" style={{ display: "none" }} aria-hidden="true" />
      </form>
    </Modal>
  );
}

function EmptyState() {
  return (
    <section
      className="tile"
      style={{
        maxWidth: 720,
        display: "grid",
        gridTemplateColumns: "56px 1fr",
        gap: 18,
        alignItems: "flex-start",
        padding: 24,
      }}
    >
      <div
        className="tile-row-icon"
        style={{ width: 56, height: 56, borderRadius: 14 }}
        aria-hidden="true"
      >
        <Users2 size={28} />
      </div>
      <div>
        <h3 style={{ marginTop: 0 }}>No huntgroups yet</h3>
        <p style={{ marginTop: 0 }}>
          Add a huntgroup to distribute incoming calls across a group of
          destinations.
        </p>
      </div>
    </section>
  );
}

/**
 * Huntgroups card grid — portal-style tile layout. Each tile shows the
 * group's name, the distribution algorithm, the queue limit description
 * and a chip indicating whether at least one assigned agent is signed
 * in. Clicking the tile opens the detail editor.
 */
function HuntGroupsCardGrid({ items }: { items: HuntGroupListItem[] }) {
  const navigate = useNavigate();

  return (
    <div
      className="tile-cards"
      style={{ ["--tile-min" as never]: `${HG_CARD_MIN}px` }}
    >
      {items.map((item) => (
        <button
          key={item.huntGroupId}
          type="button"
          className="tile tile-link"
          onClick={() => navigate(`/huntgroups/${item.huntGroupId}`)}
          style={{ textAlign: "left", gap: 10, padding: 20 }}
          aria-label={`Open ${item.name}`}
        >
          <div
            style={{
              fontWeight: 700,
              fontSize: "1rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={item.name}
          >
            {item.name || "—"}
          </div>
          <div>
            <AvailabilityChip
              online={item.assignedOnline}
              total={item.assignedTotal}
            />
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              color: "var(--portal-muted)",
              fontSize: "0.85rem",
              minHeight: 18,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={item.distribution}
          >
            <ListOrdered size={14} style={{ flexShrink: 0 }} />
            {item.distribution || "No distribution"}
          </div>
          <div
            style={{
              color: "var(--portal-muted)",
              fontSize: "0.85rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={item.limit}
          >
            {item.limit || "—"}
          </div>
          <div
            style={{
              marginTop: "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              color: "var(--portal-primary)",
              fontWeight: 600,
              fontSize: "0.85rem",
            }}
          >
            Open <ChevronRight size={16} />
          </div>
        </button>
      ))}
    </div>
  );
}

/**
 * Compact row layout for huntgroups — name + availability chip +
 * distribution + queue-limit on one line. Mirrors DestinationsRowList
 * so the two overviews feel like siblings.
 */
function HuntGroupsRowList({ items }: { items: HuntGroupListItem[] }) {
  const navigate = useNavigate();

  return (
    <div className="tile-rows">
      {items.map((item) => (
        <button
          key={item.huntGroupId}
          type="button"
          className="tile tile-link"
          onClick={() => navigate(`/huntgroups/${item.huntGroupId}`)}
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 16,
            padding: "12px 18px",
            textAlign: "left",
          }}
          aria-label={`Open ${item.name}`}
        >
          <div
            style={{
              flex: "2 1 0",
              minWidth: 0,
              fontWeight: 600,
              fontSize: "1rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={item.name}
          >
            {item.name || "—"}
          </div>
          <div style={{ flexShrink: 0 }}>
            <AvailabilityChip
              online={item.assignedOnline}
              total={item.assignedTotal}
            />
          </div>
          <div
            className="tile-row-detail"
            style={{
              flex: "1 1 0",
              minWidth: 0,
              display: "flex",
              alignItems: "center",
              gap: 6,
              color: "var(--portal-muted)",
              fontSize: "0.9rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={item.distribution}
          >
            <ListOrdered size={14} style={{ flexShrink: 0 }} />
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {item.distribution || "No distribution"}
            </span>
          </div>
          <div
            className="tile-row-detail"
            style={{
              flex: "1 1 0",
              minWidth: 0,
              color: "var(--portal-muted)",
              fontSize: "0.9rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={item.limit}
          >
            {item.limit || "—"}
          </div>
          <ChevronRight
            size={16}
            style={{ color: "var(--portal-primary)", flexShrink: 0 }}
          />
        </button>
      ))}
    </div>
  );
}

/**
 * Renders "<online>/<total> available" as a pill. The pill turns
 * green as soon as a single destination is online; otherwise it
 * stays neutral grey so an empty huntgroup doesn't shout for
 * attention from across a customer's overview.
 */
function AvailabilityChip({
  online,
  total,
}: {
  online: number;
  total: number;
}) {
  const isAvailable = online > 0;
  const styles: React.CSSProperties = isAvailable
    ? {
        background: "var(--portal-status-success-soft)",
        color: "var(--portal-status-success-darker)",
        borderColor: "var(--portal-status-success)",
      }
    : {
        background: "var(--portal-surface)",
        color: "var(--portal-muted)",
        borderColor: "var(--portal-border)",
      };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 999,
        border: "1px solid",
        fontSize: "0.8rem",
        fontWeight: 600,
        ...styles,
      }}
    >
      {online}/{total} available
    </span>
  );
}
